// /settings/billing — the entry points into Stripe, and nothing more.
//
// Checkout (monthly / annual) and the customer portal are Stripe-
// hosted; this page just explains the current status and hands off.
// Deliberately no custom card forms, invoices, or plan matrices —
// the sprint's explicit boundary.

import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useParish } from "../lib/parishContext";
import { apiPostEnvelope } from "../lib/api";

type Busy = "monthly" | "annual" | "portal" | null;

function daysLeft(trialEndsAt: string): number {
  return Math.max(0, Math.ceil((Date.parse(trialEndsAt) - Date.now()) / 86_400_000));
}

function statusLine(status: string | undefined, trialEndsAt: string | null | undefined): string {
  switch (status) {
    case "trialing": {
      const n = trialEndsAt ? daysLeft(trialEndsAt) : null;
      return n === null
        ? "You're on the free pilot."
        : `You're on the free pilot — ${n} ${n === 1 ? "day" : "days"} left.`;
    }
    case "active":
      return "Your subscription is active. Thank you for supporting Sacristy AI.";
    case "past_due":
      return "Your pilot has ended (or a payment didn't go through). Your schedule stays visible; add billing to turn the assistant back on.";
    case "canceled":
      return "Your subscription is canceled. Reactivate anytime — your data is right where you left it.";
    case "grandfathered":
      return "This parish is a founding pilot partner — Sacristy AI is free for you, forever.";
    default:
      return "Finish setting up your parish to start the free pilot.";
  }
}

export default function Billing() {
  const { parish } = useParish();
  const [searchParams] = useSearchParams();
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);

  const checkoutResult = searchParams.get("checkout");
  const grandfathered = parish.status === "grandfathered";
  const active = parish.status === "active";

  async function startCheckout(plan: "monthly" | "annual") {
    setBusy(plan);
    setError(null);
    try {
      const env = await apiPostEnvelope<{ url: string }>("/billing/checkout", { plan });
      if (env.data?.url) {
        window.location.href = env.data.url;
        return;
      }
      setError(env.error?.message ?? "Couldn't start checkout. Please try again.");
    } catch {
      setError("Network problem — please try again.");
    }
    setBusy(null);
  }

  async function openPortal() {
    setBusy("portal");
    setError(null);
    try {
      const env = await apiPostEnvelope<{ url: string }>("/billing/portal", {});
      if (env.data?.url) {
        window.location.href = env.data.url;
        return;
      }
      setError(env.error?.message ?? "Couldn't open the billing portal. Please try again.");
    } catch {
      setError("Network problem — please try again.");
    }
    setBusy(null);
  }

  return (
    <main className="page">
      <Link to="/settings" className="back-link">
        ← Settings
      </Link>
      <section className="settings__section">
        <h2 className="settings__section-title">Billing</h2>
        <p className="settings__section-lede">{statusLine(parish.status, parish.trial_ends_at)}</p>

        {checkoutResult === "success" ? (
          <p className="sign-in__confirmation billing__notice">
            You're all set — billing is active. It can take a moment to reflect here.
          </p>
        ) : null}
        {checkoutResult === "canceled" ? (
          <p className="settings__hint">Checkout was canceled — nothing was charged.</p>
        ) : null}

        {!grandfathered && !active ? (
          <>
            <div className="billing__plans">
              <button
                type="button"
                className="btn"
                disabled={busy !== null}
                onClick={() => startCheckout("monthly")}
              >
                {busy === "monthly" ? "Opening checkout…" : "$49 / month"}
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy !== null}
                onClick={() => startCheckout("annual")}
              >
                {busy === "annual" ? "Opening checkout…" : "$490 / year (2 months free)"}
              </button>
            </div>
            <p className="settings__hint">
              Everything included. No card was needed for the pilot; billing starts when your
              pilot ends. First 25 parishes: enter code <strong>FOUNDING</strong> at checkout
              to lock in $29/month forever.
            </p>
          </>
        ) : null}

        {parish.has_billing ? (
          <div className="billing__portal">
            <button
              type="button"
              className="btn btn--ghost"
              disabled={busy !== null}
              onClick={openPortal}
            >
              {busy === "portal" ? "Opening…" : "Manage billing"}
            </button>
            <p className="settings__hint">
              Card changes, invoices, and cancellation live in the secure Stripe portal.
            </p>
          </div>
        ) : null}

        {error ? <p className="sign-in__error">{error}</p> : null}
      </section>
    </main>
  );
}
